import { fetchApi } from '@/config/api';
import type { GammeComplete, CreateGammeData, UpdateGammeData } from '@/types';

// Récupérer toutes les gammes complètes d'un vendeur
export async function getGammesCompletesByVendeur(idVendeur: number): Promise<GammeComplete[]> {
  const query = `
    query GetDeclinaisonsByVendeur($idVendeur: Int!) {
      declinaisonsByVendeur(idVendeur: $idVendeur) {
        id_declinaison
        titre
        d1_FR
        d2_FR
        d3_FR
        d4_FR
        d5_FR
        d6_FR
        d7_FR
        d8_FR
        d9_FR
        d10_FR
        d11_FR
        d12_FR
      }
    }
  `;

  const variables = { idVendeur };

  const response = await fetchApi(query, variables);
  return response.declinaisonsByVendeur;
}

// Récupérer toutes les déclinaisons existantes (pour les templates)
export async function getAllDeclinaisons(): Promise<GammeComplete[]> {
  const query = `
    query GetAllDeclinaisons {
      allDeclinaisons {
        id_declinaison
        titre
        d1_FR
        d2_FR
        d3_FR
        d4_FR
        d5_FR
        d6_FR
        d7_FR
        d8_FR
        d9_FR
        d10_FR
        d11_FR
        d12_FR
      }
    }
  `;

  const response = await fetchApi(query, {});
  return response.allDeclinaisons;
}

// Récupérer une déclinaison par ID avec ses tailles
export async function getDeclinaisonById(id: number): Promise<GammeComplete | null> {
  const query = `
    query GetDeclinaison($id: Int!) {
      declinaison(id: $id) {
        id_declinaison
        titre
        d1_FR
        d2_FR
        d3_FR
        d4_FR
        d5_FR
        d6_FR
        d7_FR
        d8_FR
        d9_FR
        d10_FR
        d11_FR
        d12_FR
      }
    }
  `;

  try {
    const response = await fetchApi<{ declinaison: GammeComplete | null }>(query, { id });
    return response.declinaison;
  } catch (e) {
    return null;
  }
}

// Créer une nouvelle gamme complète
export async function createGammeComplete(data: CreateGammeData, idVendeur: number): Promise<GammeComplete> {
  const mutation = `
    mutation CreateDeclinaison($createDeclinaisonInput: CreateDeclinaisonInput!) {
      createDeclinaison(createDeclinaisonInput: $createDeclinaisonInput) {
        id_declinaison
        titre
        d1_FR
        d2_FR
        d3_FR
        d4_FR
        d5_FR
        d6_FR
        d7_FR
        d8_FR
        d9_FR
        d10_FR
        d11_FR
        d12_FR
      }
    }
  `;

  const variables = {
    createDeclinaisonInput: {
      id_vendeur: idVendeur,
      titre: data.titre,
      d1_FR: data.d1_FR,
      d2_FR: data.d2_FR,
      d3_FR: data.d3_FR,
      d4_FR: data.d4_FR,
      d5_FR: data.d5_FR,
      d6_FR: data.d6_FR,
      d7_FR: data.d7_FR,
      d8_FR: data.d8_FR,
      d9_FR: data.d9_FR,
      d10_FR: data.d10_FR,
      d11_FR: data.d11_FR,
      d12_FR: data.d12_FR,
    }
  };

  const response = await fetchApi(mutation, variables);
  return response.createDeclinaison;
}

// Mettre à jour une gamme complète (titre + colonnes d1-d12)
export async function updateGammeComplete(input: UpdateGammeData): Promise<GammeComplete> {
  const mutation = `
    mutation UpdateDeclinaison($updateDeclinaisonInput: UpdateDeclinaisonInput!) {
      updateDeclinaison(updateDeclinaisonInput: $updateDeclinaisonInput) {
        id_declinaison
        titre
        d1_FR
        d2_FR
        d3_FR
        d4_FR
        d5_FR
        d6_FR
        d7_FR
        d8_FR
        d9_FR
        d10_FR
        d11_FR
        d12_FR
      }
    }
  `;

  const variables = { updateDeclinaisonInput: input };

  const response = await fetchApi(mutation, variables);
  return response.updateDeclinaison;
}

export async function declinaisonHasProducts(id: number): Promise<boolean> {
  const query = `
    query DeclinaisonHasProducts($id: Int!) {
      declinaisonHasProducts(id: $id)
    }
  `;

  const response = await fetchApi(query, { id: Number(id) });
  return response.declinaisonHasProducts;
}

export async function deleteGammeComplete(
  id: number,
  replacementId: number | null
): Promise<boolean> {
  const mutation = `
    mutation DeleteDeclinaison($id: Int!, $replacementId: Int) {
      deleteDeclinaison(id: $id, replacementId: $replacementId)
    }
  `;

  const response = await fetchApi(mutation, {
    id: Number(id),
    replacementId: replacementId === null ? null : Number(replacementId),
  });
  return response.deleteDeclinaison;
}
